import { createSelector } from "reselect";

import { getBusinessRelations, getSaleSubTypes, getSpecies } from "selectors";
import { selectBusinessesAggridDataById } from "selectors/aggrid/businesses";
import { businessRelations as businessRelationTypes } from "constants/businesses";

// TODO - when Businesses, and the return value from the businesses aggrid selector, is fully typed out, use it here, use it.
interface BusinessDataPacket {
  id: string;
  name: string;
  [key: string]: any;
}

export interface BusinessRelationAgGridRowType {
  relatedFromBusiness: BusinessDataPacket;
  relatedToBusiness: BusinessDataPacket;
  relationship: {
    id: string;
    relationType: string;
    rateType: string;
    rateValue: string; // decimal
    saleType: string;
    saleSubType: SaleSubType;
    species: Species;
    longDescription: string;
  };
}

function buildDescription(
  relationType: string,
  relatedFromName: string,
  relationToName: string,
): string {
  switch (relationType) {
    case businessRelationTypes.CARTAGE_COLLECTION:
      return `${relatedFromName} uses ${relationToName} for ${relationType}.`;
    case businessRelationTypes.OTHER:
      return `${relatedFromName} has a relationship with ${relationToName}.`;
    case businessRelationTypes.BUYERS_PURCHASE_REBATE_AGENT:
    case businessRelationTypes.LISTING_AGENT:
    case businessRelationTypes.LIVESTOCK_CONSULTANT:
    case businessRelationTypes.OUTSIDE_AGENT:
    case businessRelationTypes.STOCK_AGENT:
    case businessRelationTypes.REBATE_AGENT:
    default: // fallback in case of new relation types - but they should be considered on addition anyway.
      return `${relatedFromName} uses ${relationToName} as their ${relationType}.`;
  }
}

export const selectBusinessRelationsAggridData = createSelector(
  [
    getBusinessRelations,
    getSpecies,
    getSaleSubTypes,
    selectBusinessesAggridDataById,
  ],
  (businessRelations, species, saleSubTypes, businessesAggridDataById) =>
    Object.values(businessRelations).map(
      (businessRelation: BusinessRelation): BusinessRelationAgGridRowType => {
        const saleSubType = saleSubTypes[businessRelation.saleSubTypeId];
        const longDescription = buildDescription(
          businessRelation.relationType,
          businessesAggridDataById[businessRelation.relatedFromId]?.business
            .name || "(Unknown Business)",
          businessesAggridDataById[businessRelation.relatedToId]?.business
            .name || "(Unknown Business)",
        );

        return {
          relatedFromBusiness:
            businessesAggridDataById[businessRelation.relatedFromId] || null,
          relatedToBusiness:
            businessesAggridDataById[businessRelation.relatedToId] || null,
          relationship: {
            id: businessRelation.id,
            relationType: businessRelation.relationType,
            rateType: businessRelation.rateType,
            rateValue: businessRelation.rateValue,
            saleType: businessRelation.saleType,
            saleSubType,
            species: species[businessRelation.speciesId],
            longDescription,
          },
        };
      },
    ),
);
